
import { useState, useEffect } from 'react';
import Button from '../../components/base/Button';
import SEO from '../../components/SEO';

export default function Courses() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedCourse, setExpandedCourse] = useState<number | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const philosophicalQuestions = [
    "What if learning could rewrite your destiny?",
    "Can 8 weeks change a lifetime?",
    "What happens when passion meets expertise?",
    "How do you build the future you want to live in?",
    "What if your next skill unlocks infinite possibilities?"
  ];

  const categories = [
    { id: 'all', name: 'All Transformations', icon: 'ri-rocket-line' },
    { id: 'tech', name: 'Code Revolution', icon: 'ri-code-line' },
    { id: 'design', name: 'Creative Mastery', icon: 'ri-palette-line' },
    { id: 'business', name: 'Strategic Thinking', icon: 'ri-lightbulb-line' }
  ];

  const courses = [
    {
      id: 1,
      title: "Product Design Mastery",
      category: "design",
      duration: "8 weeks",
      price: "₦350,000",
      intensity: "Revolutionary",
      image: "https://readdy.ai/api/search-image?query=African%20UX%20UI%20designer%20working%20on%20futuristic%20interface%20design%2C%20holographic%20wireframes%20and%20prototypes%2C%20purple%20neon%20lighting%2C%20cyberpunk%20design%20workspace%2C%20revolutionary%20creative%20environment%2C%20cinematic%20photography&width=400&height=300&seq=10&orientation=landscape",
      description: "Transform from designer to design revolutionary. Master the art of creating experiences that change lives.",
      features: ["Mind-bending design sessions", "Portfolio that stops traffic", "Industry legends as mentors", "Guaranteed transformation"],
      transformation: "From creative to design revolutionary",
      gradient: "from-pink-500 to-purple-600"
    },
    {
      id: 2,
      title: "Product Strategy Warfare",
      category: "business",
      duration: "6 weeks",
      price: "₦300,000",
      intensity: "Intense",
      image: "https://readdy.ai/api/search-image?query=African%20product%20strategist%20commanding%20war%20room%20with%20holographic%20data%20displays%2C%20strategic%20planning%20boards%2C%20futuristic%20business%20environment%2C%20purple%20and%20blue%20lighting%2C%20leadership%20revolution%20focus%2C%20cinematic%20photography&width=400&height=300&seq=11&orientation=landscape",
      description: "Learn to build products that don't just succeed — they dominate markets and change industries.",
      features: ["Strategic warfare tactics", "Market domination methods", "Leadership transformation", "Empire building skills"],
      transformation: "From manager to market conqueror",
      gradient: "from-blue-500 to-cyan-600"
    },
    {
      id: 3,
      title: "Frontend Revolution",
      category: "tech",
      duration: "8 weeks",
      price: "₦400,000",
      intensity: "Explosive",
      image: "https://readdy.ai/api/search-image?query=African%20frontend%20developer%20coding%20in%20futuristic%20environment%2C%20holographic%20React%20code%20floating%20in%20air%2C%20neon%20purple%20and%20blue%20lighting%2C%20revolutionary%20development%20workspace%2C%20cyberpunk%20tech%20aesthetic%2C%20cinematic%20photography&width=400&height=300&seq=12&orientation=landscape",
      description: "Build web experiences that don't just work — they mesmerize, captivate, and convert.",
      features: ["React mastery unleashed", "Design systems that scale", "Performance optimization secrets", "Code that tells stories"],
      transformation: "From coder to frontend architect",
      gradient: "from-green-500 to-emerald-600"
    },
    {
      id: 4,
      title: "Backend Domination",
      category: "tech",
      duration: "8 weeks",
      price: "₦450,000",
      intensity: "Hardcore",
      image: "https://readdy.ai/api/search-image?query=African%20backend%20developer%20orchestrating%20server%20architecture%2C%20holographic%20database%20schemas%20and%20API%20networks%2C%20futuristic%20data%20center%20environment%2C%20purple%20lighting%2C%20system%20architecture%20mastery%2C%20cinematic%20photography&width=400&height=300&seq=13&orientation=landscape",
      description: "Master the invisible forces that power the digital world. Build systems that never break.",
      features: ["Server architecture mastery", "Database wizardry", "API design perfection", "Cloud infrastructure control"],
      transformation: "From developer to system architect",
      gradient: "from-orange-500 to-red-600"
    },
    {
      id: 5,
      title: "Full-Stack Supremacy",
      category: "tech",
      duration: "12 weeks",
      price: "₦500,000",
      intensity: "Ultimate",
      image: "https://readdy.ai/api/search-image?query=African%20full-stack%20developer%20commanding%20complete%20web%20ecosystem%2C%20frontend%20and%20backend%20holographic%20displays%2C%20comprehensive%20tech%20mastery%20environment%2C%20purple%20and%20blue%20neon%2C%20ultimate%20development%20setup%2C%20cinematic%20photography&width=400&height=300&seq=14&orientation=landscape",
      description: "Become the complete digital architect. Master every layer of the web from pixels to databases.",
      features: ["Complete stack mastery", "DevOps revolution", "Team leadership skills", "Industry certification"],
      transformation: "From developer to digital architect",
      gradient: "from-purple-500 to-indigo-600"
    },
    {
      id: 6,
      title: "Mobile App Domination",
      category: "tech",
      duration: "10 weeks",
      price: "₦450,000",
      intensity: "Intense",
      image: "https://readdy.ai/api/search-image?query=African%20mobile%20app%20developer%20creating%20revolutionary%20apps%2C%20holographic%20mobile%20interfaces%20and%20Flutter%20code%2C%20futuristic%20mobile%20development%20lab%2C%20purple%20neon%20lighting%2C%20app%20store%20domination%20theme%2C%20cinematic%20photography&width=400&height=300&seq=15&orientation=landscape",
      description: "Build mobile experiences that users can't live without. Dominate app stores worldwide.",
      features: ["Flutter & Swift mastery", "App store optimization", "User experience perfection", "Performance optimization"],
      transformation: "From developer to app store conqueror",
      gradient: "from-teal-500 to-blue-600"
    },
    {
      id: 7,
      title: "Cloud Infrastructure Mastery",
      category: "tech",
      duration: "6 weeks",
      price: "₦350,000",
      intensity: "Advanced",
      image: "https://readdy.ai/api/search-image?query=African%20DevOps%20engineer%20controlling%20cloud%20infrastructure%2C%20holographic%20AWS%20and%20Azure%20dashboards%2C%20futuristic%20server%20monitoring%20center%2C%20purple%20accent%20lighting%2C%20cloud%20mastery%20environment%2C%20cinematic%20photography&width=400&height=300&seq=16&orientation=landscape",
      description: "Command the cloud. Build infrastructure that scales infinitely and never fails.",
      features: ["AWS & Azure mastery", "Container orchestration", "CI/CD perfection", "Infrastructure as code"],
      transformation: "From engineer to cloud architect",
      gradient: "from-sky-500 to-blue-600"
    },
    {
      id: 8,
      title: "Cybersecurity Warfare",
      category: "tech",
      duration: "8 weeks",
      price: "₦400,000",
      intensity: "Elite",
      image: "https://readdy.ai/api/search-image?query=African%20cybersecurity%20specialist%20in%20futuristic%20security%20operations%20center%2C%20holographic%20threat%20analysis%20displays%2C%20advanced%20security%20monitoring%2C%20purple%20and%20red%20lighting%2C%20digital%20warfare%20environment%2C%20cinematic%20photography&width=400&height=300&seq=17&orientation=landscape",
      description: "Become the digital guardian. Protect systems, hunt threats, and secure the future.",
      features: ["Ethical hacking mastery", "Threat hunting skills", "Security architecture", "Digital forensics"],
      transformation: "From security analyst to cyber warrior",
      gradient: "from-red-500 to-pink-600"
    },
    {
      id: 9,
      title: "AI Revolution",
      category: "tech",
      duration: "10 weeks",
      price: "₦500,000",
      intensity: "Futuristic",
      image: "https://readdy.ai/api/search-image?query=African%20AI%20researcher%20working%20with%20holographic%20neural%20networks%2C%20machine%20learning%20models%20floating%20in%20space%2C%20futuristic%20AI%20laboratory%2C%20purple%20and%20blue%20neon%2C%20artificial%20intelligence%20mastery%2C%20cinematic%20photography&width=400&height=300&seq=18&orientation=landscape",
      description: "Build the future with artificial intelligence. Create systems that think, learn, and evolve.",
      features: ["Neural network mastery", "Deep learning secrets", "AI model deployment", "Future technology"],
      transformation: "From programmer to AI architect",
      gradient: "from-violet-500 to-purple-600"
    },
    {
      id: 10,
      title: "Brand Revolution",
      category: "design",
      duration: "6 weeks",
      price: "₦300,000",
      intensity: "Creative",
      image: "https://readdy.ai/api/search-image?query=African%20graphic%20designer%20creating%20revolutionary%20brand%20identity%2C%20holographic%20logo%20designs%20and%20brand%20materials%2C%20futuristic%20creative%20studio%2C%20purple%20accent%20lighting%2C%20brand%20mastery%20environment%2C%20cinematic%20photography&width=400&height=300&seq=19&orientation=landscape",
      description: "Create brands that don't just exist — they dominate minds and capture hearts.",
      features: ["Brand psychology mastery", "Visual identity perfection", "Logo design secrets", "Brand strategy warfare"],
      transformation: "From designer to brand architect",
      gradient: "from-amber-500 to-orange-600"
    },
    {
      id: 11,
      title: "Motion Graphics Mastery",
      category: "design",
      duration: "8 weeks",
      price: "₦350,000",
      intensity: "Dynamic",
      image: "https://readdy.ai/api/search-image?query=African%20motion%20graphics%20artist%20creating%20holographic%20animations%2C%203D%20models%20and%20effects%20floating%20in%20space%2C%20futuristic%20animation%20studio%2C%20purple%20and%20blue%20lighting%2C%20motion%20mastery%20environment%2C%20cinematic%20photography&width=400&height=300&seq=20&orientation=landscape",
      description: "Bring static designs to life. Create motion that mesmerizes and stories that captivate.",
      features: ["After Effects mastery", "3D animation secrets", "Visual storytelling", "Motion design psychology"],
      transformation: "From designer to motion architect",
      gradient: "from-emerald-500 to-teal-600"
    },
    {
      id: 12,
      title: "Copywriting Revolution",
      category: "business",
      duration: "4 weeks",
      price: "₦250,000",
      intensity: "Persuasive",
      image: "https://readdy.ai/api/search-image?query=African%20copywriter%20crafting%20revolutionary%20content%2C%20holographic%20words%20and%20persuasion%20techniques%20floating%20around%2C%20futuristic%20writing%20workspace%2C%20purple%20lighting%2C%20content%20mastery%20environment%2C%20cinematic%20photography&width=400&height=300&seq=21&orientation=landscape",
      description: "Master the art of persuasion. Write words that don't just inform — they transform.",
      features: ["Psychology of persuasion", "Brand voice mastery", "Conversion optimization", "Storytelling secrets"],
      transformation: "From writer to persuasion architect",
      gradient: "from-rose-500 to-pink-600"
    },
    {
      id: 13,
      title: "Virtual Assistance Mastery",
      category: "business",
      duration: "6 weeks",
      price: "₦200,000",
      intensity: "Professional",
      image: "https://readdy.ai/api/search-image?query=African%20virtual%20assistant%20managing%20multiple%20holographic%20screens%20and%20digital%20workflows%2C%20futuristic%20remote%20work%20environment%2C%20organized%20digital%20workspace%2C%20purple%20and%20blue%20lighting%2C%20professional%20efficiency%20mastery%2C%20cinematic%20photography&width=400&height=300&seq=22&orientation=landscape",
      description: "Become the ultimate digital support system. Master remote work and client management like never before.",
      features: ["Advanced productivity tools", "Client relationship mastery", "Digital workflow optimization", "Remote work excellence"],
      transformation: "From assistant to virtual operations expert",
      gradient: "from-indigo-500 to-blue-600"
    },
    {
      id: 14,
      title: "Data Analysis Revolution",
      category: "tech",
      duration: "8 weeks",
      price: "₦380,000",
      intensity: "Analytical",
      image: "https://readdy.ai/api/search-image?query=African%20data%20analyst%20working%20with%20holographic%20data%20visualizations%20and%20statistical%20models%2C%20futuristic%20analytics%20laboratory%2C%20floating%20charts%20and%20graphs%2C%20purple%20neon%20lighting%2C%20data%20science%20mastery%20environment%2C%20cinematic%20photography&width=400&height=300&seq=23&orientation=landscape",
      description: "Transform raw data into powerful insights. Become the oracle that guides business decisions.",
      features: ["Python & R mastery", "Advanced visualization", "Statistical modeling", "Business intelligence"],
      transformation: "From analyst to data scientist",
      gradient: "from-cyan-500 to-blue-600"
    },
    {
      id: 15,
      title: "Photography & Videography Mastery",
      category: "design",
      duration: "10 weeks",
      price: "₦320,000",
      intensity: "Creative",
      image: "https://readdy.ai/api/search-image?query=African%20photographer%20and%20videographer%20in%20futuristic%20studio%20with%20holographic%20editing%20interfaces%2C%20professional%20camera%20equipment%2C%20creative%20lighting%20setup%2C%20purple%20accent%20lighting%2C%20visual%20storytelling%20mastery%2C%20cinematic%20photography&width=400&height=300&seq=24&orientation=landscape",
      description: "Capture moments that tell stories. Create visual content that stops scrolling and starts conversations.",
      features: ["Professional shooting techniques", "Advanced editing mastery", "Brand storytelling", "Commercial photography"],
      transformation: "From photographer to visual storyteller",
      gradient: "from-yellow-500 to-orange-600"
    },
    {
      id: 16,
      title: "No-Code Development Revolution",
      category: "tech",
      duration: "6 weeks",
      price: "₦280,000",
      intensity: "Innovative",
      image: "https://readdy.ai/api/search-image?query=African%20no-code%20developer%20building%20applications%20with%20holographic%20drag-and-drop%20interfaces%2C%20futuristic%20visual%20development%20environment%2C%20floating%20app%20components%2C%20purple%20and%20green%20lighting%2C%20innovation%20mastery%2C%20cinematic%20photography&width=400&height=300&seq=25&orientation=landscape",
      description: "Build powerful applications without writing code. Turn ideas into reality at the speed of thought.",
      features: ["Bubble & Webflow mastery", "Database design", "Automation workflows", "App deployment"],
      transformation: "From idea to app creator",
      gradient: "from-green-500 to-teal-600"
    },
    {
      id: 17,
      title: "Forex Trading Mastery",
      category: "business",
      duration: "8 weeks",
      price: "₦400,000",
      intensity: "Strategic",
      image: "https://readdy.ai/api/search-image?query=African%20forex%20trader%20analyzing%20holographic%20currency%20charts%20and%20market%20data%2C%20futuristic%20trading%20floor%20environment%2C%20floating%20financial%20graphs%2C%20purple%20and%20gold%20lighting%2C%20financial%20mastery%20workspace%2C%20cinematic%20photography&width=400&height=300&seq=26&orientation=landscape",
      description: "Master the global currency markets. Turn market movements into consistent profits.",
      features: ["Technical analysis mastery", "Risk management strategies", "Psychology of trading", "Portfolio optimization"],
      transformation: "From trader to market strategist",
      gradient: "from-yellow-500 to-amber-600"
    }
  ];

  const filteredCourses = selectedCategory === 'all' 
    ? courses 
    : courses.filter(course => course.category === selectedCategory);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentQuestion((prev) => (prev + 1) % philosophicalQuestions.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "TheSpire Revolutionary Courses",
    "description": "Revolutionary tech and creative courses that transform minds and build the future. Learn from industry legends with hands-on transformation.",
    "url": `${import.meta.env.VITE_SITE_URL || "https://thespire.com.ng"}/courses`,
    "provider": {
      "@type": "Organization",
      "name": "TheSpire",
      "url": `${import.meta.env.VITE_SITE_URL || "https://thespire.com.ng"}`
    },
    "offers": courses.map(course => ({
      "@type": "Course",
      "name": course.title,
      "description": course.description,
      "provider": {
        "@type": "Organization",
        "name": "TheSpire"
      },
      "courseMode": "blended",
      "timeRequired": course.duration,
      "offers": {
        "@type": "Offer",
        "price": course.price.replace('₦', ''),
        "priceCurrency": "NGN"
      }
    })),
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Abuja",
      "addressCountry": "Nigeria"
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden">
      <SEO
        title="Revolutionary Courses - Transform Your Future at TheSpire"
        description="Revolutionary tech and creative courses that transform minds and build the future. Learn from industry legends with hands-on transformation."
        keywords="revolutionary courses, tech transformation, programming mastery, design revolution, nigeria tech training"
        structuredData={structuredData}
      />
      
      {/* Dynamic Background */}
      <div 
        className="fixed inset-0 opacity-30 pointer-events-none"
        style={{
          background: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(109, 3, 244, 0.3) 0%, rgba(0, 127, 255, 0.2) 50%, transparent 70%)`
        }}
      />

      {/* Floating Particles */}
      <div className="fixed inset-0 pointer-events-none">
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-purple-400 rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }}
          />
        ))}
      </div>

      {/* Revolutionary Hero */}
      <section className="relative min-h-screen flex items-center justify-center pt-16 sm:pt-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center relative z-10 w-full">
          {/* Philosophical Question */}
          <div className="mb-6 sm:mb-8 h-12 sm:h-16 flex items-center justify-center">
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-purple-300 italic transition-all duration-1000 transform px-4 text-center">
              {philosophicalQuestions[currentQuestion]}
            </p>
          </div>

          {/* Main Title */}
          <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-8xl xl:text-9xl font-black mb-6 sm:mb-8 leading-none">
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 animate-pulse">
              TRANSFORM
            </span>
            <span className="block text-white mt-2 sm:mt-4">
              EVERYTHING
            </span>
          </h1>

          <div className="max-w-4xl mx-auto mb-8 sm:mb-12 px-4">
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-300 leading-relaxed">
              Don't just learn skills — 
              <span className="text-purple-400 font-semibold"> undergo complete transformation.</span>
              <br />
              <span className="text-blue-400 font-semibold">8 weeks to rewrite your destiny.</span>
            </p>
          </div>

          {/* Animated Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8 mb-8 sm:mb-12 px-4">
            <div className="text-center">
              <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-black text-purple-400 mb-2">1000+</div>
              <div className="text-xs sm:text-sm text-gray-400">Lives Transformed</div>
            </div>
            <div className="text-center">
              <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-black text-blue-400 mb-2">95%</div>
              <div className="text-xs sm:text-sm text-gray-400">Career Advancement</div>
            </div>
            <div className="text-center">
              <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-black text-pink-400 mb-2">8</div>
              <div className="text-xs sm:text-sm text-gray-400">Weeks to Mastery</div>
            </div>
            <div className="text-center">
              <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-black text-cyan-400 mb-2">∞</div>
              <div className="text-xs sm:text-sm text-gray-400">Possibilities Unlocked</div>
            </div>
          </div>

          {/* CTA */}
          <a 
            href="https://wa.me/2349167710173?text=I'm%20ready%20to%20begin%20my%20transformation%20with%20TheSpire%20courses!"
            target="_blank"
            rel="noopener noreferrer"
            className="group relative px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full font-bold text-sm sm:text-base lg:text-lg overflow-hidden transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer inline-block"
          >
            <span className="relative z-10">Begin Your Transformation</span>
            <div className="absolute inset-0 bg-gradient-to-r from-pink-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </a>
        </div>

        {/* Geometric Shapes */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-16 h-16 sm:w-32 sm:h-32 border border-purple-500/30 rotate-45 animate-spin-slow"></div>
          <div className="absolute bottom-1/4 right-1/4 w-12 h-12 sm:w-24 sm:h-24 border border-blue-500/30 rotate-12 animate-bounce"></div>
          <div className="absolute top-1/2 right-1/3 w-8 h-8 sm:w-16 sm:h-16 bg-gradient-to-r from-purple-500/20 to-blue-50/20 rounded-full animate-pulse"></div>
        </div>
      </section>

      {/* Revolutionary Categories */}
      <section className="py-8 sm:py-12 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`group relative px-4 sm:px-6 py-3 sm:py-4 rounded-2xl font-bold transition-all duration-300 whitespace-nowrap cursor-pointer overflow-hidden text-xs sm:text-sm lg:text-base ${
                  selectedCategory === category.id
                    ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-2xl scale-105'
                    : 'bg-gray-800/50 text-gray-300 hover:bg-gray-700/50 border border-gray-600/50'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <i className={`${category.icon} text-sm sm:text-base lg:text-lg`}></i>
                  <span>{category.name}</span>
                </div>
                {selectedCategory === category.id && (
                  <div className="absolute inset-0 bg-gradient-to-r from-pink-600/20 to-purple-600/20 animate-pulse"></div>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Revolutionary Courses Grid */}
      <section className="py-12 sm:py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {filteredCourses.map((course) => (
              <div 
                key={course.id}
                className="group relative overflow-hidden rounded-3xl bg-gradient-to-br from-gray-900/80 to-gray-800/80 backdrop-blur-sm border border-gray-700/50 hover:scale-105 transition-all duration-500"
              >
                {/* Intensity Badge */}
                <div className={`absolute top-3 sm:top-4 right-3 sm:right-4 z-20 px-2 sm:px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r ${course.gradient} text-white`}>
                  {course.intensity}
                </div>

                <div className="relative overflow-hidden">
                  <img 
                    src={course.image}
                    alt={course.title}
                    className="w-full h-32 sm:h-40 lg:h-48 object-cover object-top transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                  
                  {/* Duration Badge */}
                  <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 bg-black/50 backdrop-blur-sm text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold">
                    {course.duration}
                  </div>
                </div>
                
                <div className="p-4 sm:p-6 relative">
                  <h3 className="text-base sm:text-lg lg:text-xl font-bold text-white mb-2 sm:mb-3">{course.title}</h3>
                  <p className="text-gray-300 mb-3 sm:mb-4 leading-relaxed text-xs sm:text-sm">{course.description}</p>
                  
                  <div className="mb-3 sm:mb-4">
                    <div className="flex justify-between items-center mb-2 sm:mb-3">
                      <span className={`text-lg sm:text-xl lg:text-2xl font-black bg-gradient-to-r ${course.gradient} bg-clip-text text-transparent`}>
                        {course.price}
                      </span>
                      <button
                        onClick={() => setExpandedCourse(expandedCourse === course.id ? null : course.id)}
                        className="text-purple-400 hover:text-purple-300 transition-colors cursor-pointer"
                      >
                        <i className={`ri-${expandedCourse === course.id ? 'subtract' : 'add'}-line text-base sm:text-lg lg:text-xl`}></i>
                      </button>
                    </div>
                    
                    <div className={`transition-all duration-300 overflow-hidden ${
                      expandedCourse === course.id ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'
                    }`}>
                      <div className="space-y-2 sm:space-y-3 mb-3 sm:mb-4">
                        {course.features.map((feature, index) => (
                          <div key={index} className="flex items-center text-xs sm:text-sm text-gray-300">
                            <i className="ri-check-line text-purple-400 mr-2"></i>
                            {feature}
                          </div>
                        ))}
                      </div>
                      <div className={`text-xs font-semibold bg-gradient-to-r ${course.gradient} bg-clip-text text-transparent`}>
                        {course.transformation}
                      </div>
                    </div>
                  </div>
                  
                  <a 
                    href={`https://wa.me/2349167710173?text=I'm%20interested%20in%20enrolling%20for%20${encodeURIComponent(course.title)}%20course.%20Please%20send%20me%20more%20details!`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-full bg-gradient-to-r ${course.gradient} hover:scale-105 transition-transform duration-300 px-4 sm:px-6 py-2 sm:py-3 text-xs sm:text-sm lg:text-base rounded-xl font-medium text-white text-center block whitespace-nowrap cursor-pointer`}
                  >
                    Begin Transformation
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Revolutionary Features */}
      <section className="py-12 sm:py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400 mb-4 sm:mb-6">
              THE REVOLUTION
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-300">Why TheSpire transforms lives, not just skills</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {[
              { icon: "ri-live-line", title: "Live Transformation", desc: "Real-time evolution with industry legends", color: "from-purple-500 to-pink-500" },
              { icon: "ri-user-star-line", title: "Legendary Mentors", desc: "Learn from those who built the future", color: "from-blue-500 to-cyan-500" },
              { icon: "ri-award-line", title: "Revolutionary Certification", desc: "Credentials that open every door", color: "from-green-500 to-emerald-500" },
              { icon: "ri-briefcase-line", title: "Career Domination", desc: "Placement in roles that matter", color: "from-orange-500 to-red-500" }
            ].map((feature, index) => (
              <div key={index} className="group text-center relative">
                <div className={`w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 bg-gradient-to-r ${feature.color} rounded-3xl flex items-center justify-center mx-auto mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`${feature.icon} text-lg sm:text-2xl lg:text-3xl text-white`}></i>
                </div>
                <h3 className="text-base sm:text-lg lg:text-xl font-bold text-white mb-2 sm:mb-3">{feature.title}</h3>
                <p className="text-gray-300 leading-relaxed text-sm sm:text-base">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Revolutionary CTA */}
      <section className="py-12 sm:py-20 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/50 to-blue-900/50"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-black text-white mb-6 sm:mb-8 leading-tight">
            YOUR TRANSFORMATION
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400">
              STARTS NOW
            </span>
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-300 mb-8 sm:mb-12">
            The future belongs to those who transform today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center">
            <a 
              href="https://wa.me/2349167710173?text=I'm%20ready%20to%20choose%20my%20transformation%20course%20with%20TheSpire!"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full font-bold text-sm sm:text-base lg:text-lg overflow-hidden transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer w-full sm:w-auto text-center"
            >
              <span className="relative z-10">Choose Your Transformation</span>
              <div className="absolute inset-0 bg-gradient-to-r from-pink-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </a>
            <a href="/contact" className="px-6 sm:px-8 py-3 sm:py-4 border-2 border-purple-400 rounded-full font-bold text-sm sm:text-base lg:text-lg hover:bg-purple-400 hover:text-black transition-all duration-300 whitespace-nowrap cursor-pointer w-full sm:w-auto text-center">
              Speak to a Revolutionary
            </a>
          </div>
        </div>
      </section>

      {/* WhatsApp Floating Button */}
      <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50">
        <a
          href="https://wa.me/2349167710173"
          target="_blank"
          rel="noopener noreferrer"
          className="w-12 h-12 sm:w-14 sm:h-14 bg-green-500 rounded-full flex items-center justify-center shadow-lg hover:bg-green-600 transition-colors group"
        >
          <i className="ri-whatsapp-line text-white text-lg sm:text-xl lg:text-2xl group-hover:scale-110 transition-transform"></i>
        </a>
      </div>
    </div>
  );
}
